import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from 'hooks'
import { useAutonomousWakeup } from '../hooks/useAutonomousWakeup'
import type { AgenticHubState, SearchFormValues } from '../store/agenticHubSlice'

const STORAGE_KEY = 'agentic_hub_wakeup_position'

function getTabDisplayName(tabId: string, title: string, searchForm: SearchFormValues | null): string {
    if (title && title !== 'New Conversation') return title
    if (!searchForm) return tabId.slice(0, 8)
    const { originOptions, destinationOptions, equipment } = searchForm
    if (!originOptions.length) return tabId.slice(0, 8)
    const origin = originOptions.map((o) => o.label).join(', ')
    const dest = destinationOptions.length ? destinationOptions.map((o) => o.label).join(', ') : 'Anywhere'
    const parts = [`${origin} → ${dest}`]
    if (equipment.length) parts.push(equipment.map((id) => id.replace(/-\d$/, '')).join(', '))
    return parts.join(' • ')
}

function useDraggable(defaultPos: { x: number; y: number }) {
    const [position, setPosition] = useState<{ x: number; y: number }>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY)
            if (saved) return JSON.parse(saved)
        } catch {
            /* ignore */
        }
        return defaultPos
    })
    const [isDragging, setIsDragging] = useState(false)
    const dragStartRef = useRef<{ mouseX: number; mouseY: number; posX: number; posY: number } | null>(null)

    const onMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            setIsDragging(true)
            dragStartRef.current = { mouseX: e.clientX, mouseY: e.clientY, posX: position.x, posY: position.y }
        },
        [position]
    )

    useEffect(() => {
        if (!isDragging) return

        const onMouseMove = (e: MouseEvent) => {
            if (!dragStartRef.current) return
            const dx = e.clientX - dragStartRef.current.mouseX
            const dy = e.clientY - dragStartRef.current.mouseY
            const newX = Math.max(0, Math.min(dragStartRef.current.posX + dx, window.innerWidth - 60))
            const newY = Math.max(0, Math.min(dragStartRef.current.posY + dy, window.innerHeight - 40))
            setPosition({ x: newX, y: newY })
        }

        const onMouseUp = () => {
            setIsDragging(false)
            dragStartRef.current = null
            setPosition((pos) => {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(pos))
                return pos
            })
        }

        window.addEventListener('mousemove', onMouseMove)
        window.addEventListener('mouseup', onMouseUp)
        return () => {
            window.removeEventListener('mousemove', onMouseMove)
            window.removeEventListener('mouseup', onMouseUp)
        }
    }, [isDragging])

    return { position, isDragging, onMouseDown }
}

/**
 * Debug overlay showing autonomous wakeup countdowns per tab.
 * Only renders when `localStorage.getItem('debug_agentic_hub')` is set.
 * Mounted at layout level so it's visible on any page.
 * Draggable — position persisted in localStorage.
 */
export const AutonomousWakeupDebug = () => {
    const show = typeof window !== 'undefined' && !!localStorage.getItem('debug_agentic_hub')
    const { debugCountdowns } = useAutonomousWakeup()
    const tabs = useAppSelector((s: { agenticHub: AgenticHubState }) => s.agenticHub.tabs)
    const { position, isDragging, onMouseDown } = useDraggable({ x: 12, y: window.innerHeight - 120 })

    if (!show || Object.keys(debugCountdowns).length === 0) return null

    return (
        <div
            onMouseDown={onMouseDown}
            style={{ left: position.x, top: position.y }}
            className={`fixed z-50 rounded-lg bg-gray-900/90 text-white px-3 py-2 text-[11px] font-mono space-y-0.5 max-w-[280px] select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
            <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Autonomous Wakeup</div>
            {Object.entries(debugCountdowns).map(([tabId, secs]) => {
                const m = Math.floor(secs / 60)
                const s = secs % 60
                const tabState = tabs[tabId]
                const displayName = tabState ? getTabDisplayName(tabId, tabState.tab.title, tabState.searchForm) : tabId.slice(0, 8)
                return (
                    <div key={tabId} className="flex justify-between gap-3">
                        <span className="truncate text-gray-300">{displayName}</span>
                        <span className={secs < 60 ? 'text-amber-400' : 'text-emerald-400'}>
                            {m}:{s.toString().padStart(2, '0')}
                        </span>
                    </div>
                )
            })}
        </div>
    )
}
